export type GeoPosition = [number, number];

export interface GeoPoint {
  type: "Point";
  coordinates: GeoPosition;
}

export interface GeoPolygon {
  type: "Polygon";
  coordinates: GeoPosition[][];
}

export interface GeoMultiPolygon {
  type: "MultiPolygon";
  coordinates: GeoPosition[][][];
}

export type EventGeometry = GeoPoint | GeoPolygon | GeoMultiPolygon;

export type GeographyScope =
  | "point"
  | "area"
  | "country"
  | "multi-country"
  | "region"
  | "global"
  | "multi-location"
  | "unknown";

export type HazardCategory =
  | "seismic"
  | "weather"
  | "flood"
  | "fire"
  | "landslide"
  | "volcano"
  | "coastal"
  | "drought"
  | "health"
  | "geopolitical"
  | "infrastructure-supply-chain";

export interface AssociatedCountry {
  code: string;
  name: string;
  whoRegion?: string;
}

export interface EventGeography {
  geometry?: EventGeometry;
  scope: GeographyScope;
  country?: string;
  countryCode?: string;
  admin1?: string;
  location?: string;
  associatedCountries?: AssociatedCountry[];
  confidence: "high" | "medium" | "low";
  method: "provider-geometry" | "provider-geocoding" | "provider-title" | "provider-taxonomy" | "legacy-provider-point";
  provenance: string;
}

const EPSILON = 1e-12;
const MAX_POLYGONS = 16;
const MAX_RINGS_PER_POLYGON = 16;
const MAX_POSITIONS_PER_RING = 512;
const MAX_POSITIONS_PER_GEOMETRY = 4_096;

export function eventGeometryValidationWork(value: unknown) {
  if (!value || typeof value !== "object") return 0;
  const candidate = value as { type?: unknown; coordinates?: unknown };
  if (candidate.type === "Point") return 1;

  const polygonWork = (polygon: unknown) => {
    if (!Array.isArray(polygon)) return 1;
    const positions = polygon.reduce(
      (total, ring) => total + (Array.isArray(ring) ? ring.length : 1),
      0,
    );
    return Math.max(1, positions * positions);
  };

  if (candidate.type === "Polygon") return polygonWork(candidate.coordinates);
  if (candidate.type === "MultiPolygon" && Array.isArray(candidate.coordinates)) {
    return candidate.coordinates.reduce((total, polygon) => total + polygonWork(polygon), 0);
  }
  return 0;
}

function isPosition(value: unknown): value is GeoPosition {
  return Array.isArray(value)
    && value.length === 2
    && typeof value[0] === "number"
    && typeof value[1] === "number"
    && Number.isFinite(value[0])
    && Number.isFinite(value[1])
    && value[0] >= -180
    && value[0] <= 180
    && value[1] >= -90
    && value[1] <= 90;
}

function samePosition(left: GeoPosition, right: GeoPosition) {
  return left[0] === right[0] && left[1] === right[1];
}

function cross(a: GeoPosition, b: GeoPosition, c: GeoPosition) {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

function onSegment(point: GeoPosition, start: GeoPosition, end: GeoPosition) {
  return Math.abs(cross(start, end, point)) <= EPSILON
    && point[0] >= Math.min(start[0], end[0]) - EPSILON
    && point[0] <= Math.max(start[0], end[0]) + EPSILON
    && point[1] >= Math.min(start[1], end[1]) - EPSILON
    && point[1] <= Math.max(start[1], end[1]) + EPSILON;
}

function segmentsIntersect(a: GeoPosition, b: GeoPosition, c: GeoPosition, d: GeoPosition) {
  const abC = cross(a, b, c);
  const abD = cross(a, b, d);
  const cdA = cross(c, d, a);
  const cdB = cross(c, d, b);
  if (((abC > EPSILON && abD < -EPSILON) || (abC < -EPSILON && abD > EPSILON))
    && ((cdA > EPSILON && cdB < -EPSILON) || (cdA < -EPSILON && cdB > EPSILON))) return true;
  return (Math.abs(abC) <= EPSILON && onSegment(c, a, b))
    || (Math.abs(abD) <= EPSILON && onSegment(d, a, b))
    || (Math.abs(cdA) <= EPSILON && onSegment(a, c, d))
    || (Math.abs(cdB) <= EPSILON && onSegment(b, c, d));
}

function adjacentEdgesOverlap(a: GeoPosition, b: GeoPosition, c: GeoPosition, d: GeoPosition) {
  if (Math.abs(cross(a, b, c)) > EPSILON || Math.abs(cross(a, b, d)) > EPSILON) return false;
  const useLng = Math.abs(b[0] - a[0]) >= Math.abs(b[1] - a[1]);
  const first = [a[useLng ? 0 : 1], b[useLng ? 0 : 1]].sort((x, y) => x - y);
  const second = [c[useLng ? 0 : 1], d[useLng ? 0 : 1]].sort((x, y) => x - y);
  return Math.min(first[1], second[1]) - Math.max(first[0], second[0]) > EPSILON;
}

function signedRingArea(ring: GeoPosition[]) {
  let twiceArea = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const current = ring[index];
    const next = ring[index + 1];
    twiceArea += current[0] * next[1] - next[0] * current[1];
  }
  return twiceArea / 2;
}

function isSimpleRing(value: unknown): value is GeoPosition[] {
  if (!Array.isArray(value) || value.length < 4 || value.length > MAX_POSITIONS_PER_RING || !value.every(isPosition)) return false;
  const ring = value as GeoPosition[];
  if (!samePosition(ring[0], ring[ring.length - 1])) return false;
  if (new Set(ring.slice(0, -1).map(([lng, lat]) => `${lng},${lat}`)).size < 3) return false;
  if (Math.abs(signedRingArea(ring)) <= EPSILON) return false;

  const edgeCount = ring.length - 1;
  for (let left = 0; left < edgeCount; left += 1) {
    const a = ring[left];
    const b = ring[left + 1];
    if (samePosition(a, b)) return false;
    for (let right = left + 1; right < edgeCount; right += 1) {
      const c = ring[right];
      const d = ring[right + 1];
      const adjacent = right === left + 1 || (left === 0 && right === edgeCount - 1);
      if (adjacent) {
        if (adjacentEdgesOverlap(a, b, c, d)) return false;
      } else if (segmentsIntersect(a, b, c, d)) {
        return false;
      }
    }
  }
  return true;
}

function pointOnRing(point: GeoPosition, ring: GeoPosition[]) {
  for (let index = 0; index < ring.length - 1; index += 1) {
    if (onSegment(point, ring[index], ring[index + 1])) return true;
  }
  return false;
}

function pointInRing(point: GeoPosition, ring: GeoPosition[]) {
  if (pointOnRing(point, ring)) return false;
  let inside = false;
  for (let index = 0, previous = ring.length - 2; index < ring.length - 1; previous = index, index += 1) {
    const current = ring[index];
    const prior = ring[previous];
    if ((current[1] > point[1]) !== (prior[1] > point[1])
      && point[0] < ((prior[0] - current[0]) * (point[1] - current[1])) / (prior[1] - current[1]) + current[0]) {
      inside = !inside;
    }
  }
  return inside;
}

function ringsIntersect(left: GeoPosition[], right: GeoPosition[]) {
  for (let leftIndex = 0; leftIndex < left.length - 1; leftIndex += 1) {
    for (let rightIndex = 0; rightIndex < right.length - 1; rightIndex += 1) {
      if (segmentsIntersect(left[leftIndex], left[leftIndex + 1], right[rightIndex], right[rightIndex + 1])) return true;
    }
  }
  return false;
}

function isPolygonCoordinates(value: unknown): value is GeoPosition[][] {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_RINGS_PER_POLYGON || !value.every(isSimpleRing)) return false;
  const rings = value as GeoPosition[][];
  const outer = rings[0];
  for (let index = 1; index < rings.length; index += 1) {
    const hole = rings[index];
    if (!pointInRing(hole[0], outer) || ringsIntersect(outer, hole)) return false;
    for (let other = 1; other < index; other += 1) {
      if (ringsIntersect(hole, rings[other])
        || pointInRing(hole[0], rings[other])
        || pointInRing(rings[other][0], hole)) return false;
    }
  }
  return true;
}

export function validateEventGeometry(value: unknown): EventGeometry | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as { type?: unknown; coordinates?: unknown };
  if (candidate.type === "Point" && isPosition(candidate.coordinates)) return candidate as GeoPoint;
  if (candidate.type === "Polygon"
    && Array.isArray(candidate.coordinates)
    && candidate.coordinates.reduce((total, ring) => total + (Array.isArray(ring) ? ring.length : 0), 0) <= MAX_POSITIONS_PER_GEOMETRY
    && isPolygonCoordinates(candidate.coordinates)) return candidate as GeoPolygon;
  if (candidate.type === "MultiPolygon"
    && Array.isArray(candidate.coordinates)
    && candidate.coordinates.length > 0
    && candidate.coordinates.length <= MAX_POLYGONS
    && candidate.coordinates.reduce((total, polygon) => total + (Array.isArray(polygon)
      ? polygon.reduce((polygonTotal, ring) => polygonTotal + (Array.isArray(ring) ? ring.length : 0), 0)
      : 0), 0) <= MAX_POSITIONS_PER_GEOMETRY
    && candidate.coordinates.every(isPolygonCoordinates)) return candidate as GeoMultiPolygon;
  return undefined;
}

function geometryRings(geometry: GeoPolygon | GeoMultiPolygon) {
  return geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flatMap((polygon) => polygon);
}

function crossesAntimeridian(geometry: GeoPolygon | GeoMultiPolygon) {
  const lngs = geometryRings(geometry).flatMap((ring) => ring.map((position) => position[0]));
  return Math.max(...lngs) - Math.min(...lngs) > 180;
}

function ringCentroid(ring: GeoPosition[]): GeoPosition | undefined {
  const area = signedRingArea(ring);
  if (Math.abs(area) <= EPSILON) return undefined;
  let lng = 0;
  let lat = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const current = ring[index];
    const next = ring[index + 1];
    const factor = current[0] * next[1] - next[0] * current[1];
    lng += (current[0] + next[0]) * factor;
    lat += (current[1] + next[1]) * factor;
  }
  const scale = 1 / (6 * area);
  return [lng * scale, lat * scale];
}

export function geometryPlanarArea(geometry: EventGeometry): number | undefined {
  if (geometry.type === "Point") return 0;
  if (crossesAntimeridian(geometry)) return undefined;
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.reduce((total, polygon) => {
    const outer = Math.abs(signedRingArea(polygon[0]));
    const holes = polygon.slice(1).reduce((sum, ring) => sum + Math.abs(signedRingArea(ring)), 0);
    return total + Math.max(0, outer - holes);
  }, 0);
}

export function representativePoint(geometry: EventGeometry): GeoPosition {
  if (geometry.type === "Point") return geometry.coordinates;
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  if (crossesAntimeridian(geometry)) {
    const firstProviderPosition: GeoPosition = polygons[0][0][0];
    return firstProviderPosition;
  }
  const polygon = [...polygons].sort((left, right) => Math.abs(signedRingArea(right[0])) - Math.abs(signedRingArea(left[0])))[0];
  const centroid = ringCentroid(polygon[0]);
  if (centroid && pointInRing(centroid, polygon[0]) && polygon.slice(1).every((hole) => !pointInRing(centroid, hole) && !pointOnRing(centroid, hole))) {
    return centroid;
  }
  return polygon[0][0];
}
